import React, { useCallback, useState, useEffect } from 'react';
import { Wallpaper, Save, RefreshCw, Download, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import RoomParametersSection from './RoomParametersSection';
import WallpaperParametersSection from './WallpaperParametersSection';
import OpeningsParametersSection from './OpeningsParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';

// Импорт утилитарных функций
import { isPositiveNumber } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета обоев
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  wallpaperTypes,
  glueTypes,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Автоматический расчет периметра и площади стен при изменении размеров комнаты
  useEffect(() => {
    const length = parseFloatOrDefault(formData.roomLength, 0);
    const width = parseFloatOrDefault(formData.roomWidth, 0);
    const height = parseFloatOrDefault(formData.roomHeight, 0);
    
    if (length > 0 && width > 0) {
      const perimeter = 2 * (length + width);
      const area = perimeter * height;
      
      setFormData(prev => ({
        ...prev,
        roomPerimeter: perimeter.toFixed(2),
        roomArea: area.toFixed(2)
      }));
    }
  }, [formData.roomLength, formData.roomWidth, formData.roomHeight, setFormData]);
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем ширину рулона автоматически при изменении типа обоев
      if (name === 'wallpaperType') {
        const selectedType = wallpaperTypes.find(type => type.id === fieldValue);
        if (selectedType) {
          // Устанавливаем стандартную ширину рулона для выбранного типа
          newState.rollWidth = selectedType.defaultWidth.toString();
          
          // Обновляем рекомендуемый тип клея
          newState.glueType = selectedType.recommendedGlue;
          
          // Сбрасываем длину рулона для жидких обоев, так как расчет идет по-другому
          if (fieldValue === 'liquid') {
            newState.rollLength = '0';
          } else if (prev.rollLength === '0') {
            // Восстанавливаем стандартную длину рулона при переключении с жидких обоев
            newState.rollLength = '10.05';
          }
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeWindows' && !checked) {
        newState.windowsCount = '0';
      }
      
      if (name === 'includeDoors' && !checked) {
        newState.doorsCount = '0';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [wallpaperTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      roomLength, roomWidth, roomHeight,
      rollWidth, rollLength, patternRepeat,
      windowsCount, windowWidth, windowHeight,
      doorsCount, doorWidth, doorHeight,
      wastagePercent, 
      pricePerRoll, pricePerGlue, pricePerPrimeCoat,
      wallpaperType
    } = formData;

    // Парсинг числовых значений
    const numRoomLength = parseFloatOrDefault(roomLength, -1);
    const numRoomWidth = parseFloatOrDefault(roomWidth, -1);
    const numRoomHeight = parseFloatOrDefault(roomHeight, -1);
    
    const numRollWidth = parseFloatOrDefault(rollWidth, -1);
    const numRollLength = parseFloatOrDefault(rollLength, -1);
    const numPatternRepeat = parseFloatOrDefault(patternRepeat, 0);
    
    const numWindowsCount = parseIntOrDefault(windowsCount, 0);
    const numWindowWidth = parseFloatOrDefault(windowWidth, -1);
    const numWindowHeight = parseFloatOrDefault(windowHeight, -1);
    
    const numDoorsCount = parseIntOrDefault(doorsCount, 0);
    const numDoorWidth = parseFloatOrDefault(doorWidth, -1);
    const numDoorHeight = parseFloatOrDefault(doorHeight, -1);
    
    const numWastagePercent = parseFloatOrDefault(wastagePercent, -1);
    
    const numPricePerRoll = parseFloatOrDefault(pricePerRoll, -1);
    const numPricePerGlue = parseFloatOrDefault(pricePerGlue, -1);
    const numPricePerPrimeCoat = parseFloatOrDefault(pricePerPrimeCoat, -1);

    // Валидация размеров комнаты
    if (numRoomLength <= 0 || numRoomLength > 100) 
      newErrors.roomLength = 'Длина должна быть положительной и не более 100 м';
    
    if (numRoomWidth <= 0 || numRoomWidth > 100) 
      newErrors.roomWidth = 'Ширина должна быть положительной и не более 100 м';
    
    if (numRoomHeight <= 0 || numRoomHeight > 10) 
      newErrors.roomHeight = 'Высота должна быть положительной и не более 10 м';

    // Валидация параметров обоев (для обычных обоев)
    if (wallpaperType !== 'liquid') {
      if (numRollWidth <= 0 || numRollWidth > 500) 
        newErrors.rollWidth = 'Ширина рулона должна быть положительной и не более 500 см';
      
      if (numRollLength <= 0 || numRollLength > 50) 
        newErrors.rollLength = 'Длина рулона должна быть положительной и не более 50 м';
    }
    
    if (numPatternRepeat < 0 || numPatternRepeat > 300) 
      newErrors.patternRepeat = 'Раппорт должен быть от 0 до 300 см';

    // Валидация окон
    if (formData.includeWindows && numWindowsCount > 0) {
      if (numWindowsCount < 0 || numWindowsCount > 20) 
        newErrors.windowsCount = 'Количество окон должно быть от 0 до 20';
      
      if (numWindowWidth <= 0 || numWindowWidth > 10) 
        newErrors.windowWidth = 'Ширина окна должна быть положительной и не более 10 м';
      
      if (numWindowHeight <= 0 || numWindowHeight > 10) 
        newErrors.windowHeight = 'Высота окна должна быть положительной и не более 10 м';
    }

    // Валидация дверей
    if (formData.includeDoors && numDoorsCount > 0) {
      if (numDoorsCount < 0 || numDoorsCount > 10) 
        newErrors.doorsCount = 'Количество дверей должно быть от 0 до 10';
      
      if (numDoorWidth <= 0 || numDoorWidth > 5) 
        newErrors.doorWidth = 'Ширина двери должна быть положительной и не более 5 м';
      
      if (numDoorHeight <= 0 || numDoorHeight > 10) 
        newErrors.doorHeight = 'Высота двери должна быть положительной и не более 10 м';
      
      // Проверка высоты двери относительно высоты комнаты
      if (numDoorHeight > numRoomHeight) 
        newErrors.doorHeight = 'Высота двери не может быть больше высоты комнаты';
    }

    // Валидация процента запаса
    if (numWastagePercent < 0 || numWastagePercent > 100) 
      newErrors.wastagePercent = 'Процент запаса должен быть от 0 до 100%';

    // Валидация цен (если указаны)
    if (numPricePerRoll < 0 || numPricePerRoll > 100000) 
      newErrors.pricePerRoll = 'Цена рулона должна быть от 0 до 100 000 руб.';
    
    if (numPricePerGlue < 0 || numPricePerGlue > 10000) 
      newErrors.pricePerGlue = 'Цена клея должна быть от 0 до 10 000 руб.';
    
    if (formData.includePrimeCoat && (numPricePerPrimeCoat < 0 || numPricePerPrimeCoat > 10000)) 
      newErrors.pricePerPrimeCoat = 'Цена грунтовки должна быть от 0 до 10 000 руб.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета количества рулонов
  const calculateRollsNeeded = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const roomLength = parseFloatOrDefault(formData.roomLength);
        const roomWidth = parseFloatOrDefault(formData.roomWidth);
        const roomHeight = parseFloatOrDefault(formData.roomHeight);
        const perimeter = parseFloatOrDefault(formData.roomPerimeter);
        
        const rollWidth = parseFloatOrDefault(formData.rollWidth) / 100; // Переводим сантиметры в метры
        const rollLength = parseFloatOrDefault(formData.rollLength);
        const patternRepeat = parseFloatOrDefault(formData.patternRepeat);
        
        const wastage = parseFloatOrDefault(formData.wastagePercent) / 100; // Процент в долю
        
        const windowsCount = formData.includeWindows ? parseIntOrDefault(formData.windowsCount, 0) : 0;
        const windowWidth = parseFloatOrDefault(formData.windowWidth);
        const windowHeight = parseFloatOrDefault(formData.windowHeight);
        
        const doorsCount = formData.includeDoors ? parseIntOrDefault(formData.doorsCount, 0) : 0;
        const doorWidth = parseFloatOrDefault(formData.doorWidth);
        const doorHeight = parseFloatOrDefault(formData.doorHeight);
        
        const pricePerRoll = parseFloatOrDefault(formData.pricePerRoll);
        const pricePerGlue = parseFloatOrDefault(formData.pricePerGlue);
        const pricePerPrimeCoat = formData.includePrimeCoat ? parseFloatOrDefault(formData.pricePerPrimeCoat) : 0;
        
        // --- Расчет площади стен ---
        // Общая площадь стен
        const wallArea = perimeter * roomHeight;
        
        // Площадь окон
        const windowArea = windowsCount * windowWidth * windowHeight;
        
        // Площадь дверей
        const doorArea = doorsCount * doorWidth * doorHeight;
        
        // Площадь потолка (если включена)
        const ceilingArea = formData.includeСeiling ? roomLength * roomWidth : 0;
        
        // Чистая площадь для оклейки
        const netArea = wallArea + ceilingArea - windowArea - doorArea;
        
        // --- Расчет количества рулонов ---
        let rollsRequired = 0;
        let gluePacksRequired = 0;
        let primeCoatPacksRequired = 0;
        
        // Площадь одного рулона
        let rollArea = 0;
        
        if (formData.wallpaperType === 'liquid') {
          // Для жидких обоев расчет по площади (1 упаковка ~ на 3-4 м²)
          const liquidWallpaperCoverage = 3.5; // м² на пачку
          rollsRequired = Math.ceil(netArea / liquidWallpaperCoverage);
          
          // Клей обычно не требуется для жидких обоев
          gluePacksRequired = 0;
        } else {
          // Для обычных рулонных обоев
          
          // Количество полос из одного рулона (с учетом раппорта)
          let stripsPerRoll = 0;
          
          if (patternRepeat > 0) {
            // Если есть раппорт, учитываем его
            const patternRepeatM = patternRepeat / 100; // см в метры
            
            // Сколько целых раппортов помещается в длину рулона
            const patternsCount = Math.floor(rollLength / patternRepeatM);
            
            // Полезная длина рулона с учетом раппорта
            const usefulLength = patternsCount * patternRepeatM;
            
            // Количество полос с учетом раппорта
            stripsPerRoll = Math.floor(usefulLength / roomHeight);
          } else {
            // Если раппорта нет, просто делим длину рулона на высоту комнаты
            stripsPerRoll = Math.floor(rollLength / roomHeight);
          }
          
          // Учитываем стыки полос (не для всех типов обоев)
          let stripWidth = rollWidth;
          if (formData.wallpaperType !== 'wide' && formData.wallpaperType !== 'photoWall') {
            // Для стандартных обоев ширина полосы чуть меньше из-за нахлеста
            stripWidth = rollWidth - 0.02; // Примерно 2 см нахлест
          }
          
          // Количество полных полос на периметр
          const stripsNeeded = Math.ceil(perimeter / stripWidth);
          
          // Если отдельно клеим потолок
          const ceilingStrips = formData.includeСeiling ? Math.ceil(roomWidth / stripWidth) * Math.ceil(roomLength / roomHeight) : 0;
          
          // Общее количество полос
          const totalStrips = stripsNeeded + ceilingStrips;
          
          // Рассчитываем количество рулонов
          rollsRequired = Math.ceil(totalStrips / stripsPerRoll);
          
          // Учитываем запас материала
          rollsRequired = Math.ceil(rollsRequired * (1 + wastage));
          
          // Расчет площади одного рулона (для информации)
          rollArea = rollWidth * rollLength;
        }
        
        // --- Расчет клея и грунтовки ---
        if (formData.glueType !== 'none') {
          // Стандартная упаковка клея на ~50 м²
          const glueCoverage = formData.glueType === 'special' ? 35 : 50; // м² на пачку (специальный клей расходуется быстрее)
          gluePacksRequired = Math.ceil(netArea / glueCoverage);
        }
        
        if (formData.includePrimeCoat) {
          // Примерный расход грунтовки 100-150 г/м²
          const primeCoatCoverage = 30; // м² на литр грунтовки
          primeCoatPacksRequired = Math.ceil(netArea / primeCoatCoverage);
        }
        
        // --- Расчет стоимости ---
        const rollsCost = rollsRequired * pricePerRoll;
        const glueCost = gluePacksRequired * pricePerGlue;
        const primeCoatCost = primeCoatPacksRequired * pricePerPrimeCoat;
        const totalCost = rollsCost + glueCost + primeCoatCost;
        
        // --- Формирование результатов ---
        const calculationResults = {
          // Площади
          wallArea: wallArea,
          windowArea: windowArea,
          doorArea: doorArea,
          ceilingArea: ceilingArea,
          netArea: netArea,
          
          // Количества
          rollsRequired: rollsRequired,
          gluePacksRequired: gluePacksRequired,
          primeCoatPacksRequired: primeCoatPacksRequired,
          
          // Стоимость
          rollsCost: rollsCost,
          glueCost: glueCost,
          primeCoatCost: primeCoatCost,
          totalCost: totalCost,
          
          // Дополнительная информация
          rollArea: rollArea,
          wastageAmount: wastage,
          
          // Характеристики
          wallpaperType: formData.wallpaperType,
          glueType: formData.glueType
        };
        
        setResults(calculationResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      roomLength: '5',
      roomWidth: '4',
      roomHeight: '2.7',
      roomPerimeter: '18',
      roomArea: '48.6',
      wallpaperType: 'standard',
      rollWidth: '53',
      rollLength: '10.05',
      patternRepeat: '0',
      includeWindows: true,
      windowsCount: '1',
      windowWidth: '1.4',
      windowHeight: '1.5',
      includeDoors: true,
      doorsCount: '1',
      doorWidth: '0.9',
      doorHeight: '2.1',
      wastagePercent: '10',
      includeСeiling: false,
      glueType: 'standard',
      pricePerRoll: '1200',
      pricePerGlue: '350',
      includePrimeCoat: false,
      pricePerPrimeCoat: '400',
    });
    setResults(null);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки
    const headers = ['Параметр', 'Значение'];

    // Готовим строки данных
    const rows = [
      ['Размеры помещения', `${formData.roomLength}×${formData.roomWidth}×${formData.roomHeight} м`],
      ['Периметр стен', `${results.wallArea.toFixed(2)} м²`],
      ['Площадь стен', `${results.wallArea.toFixed(2)} м²`],
      ['Площадь окон', `${results.windowArea.toFixed(2)} м²`],
      ['Площадь дверей', `${results.doorArea.toFixed(2)} м²`],
      ['Площадь потолка', `${results.ceilingArea.toFixed(2)} м²`],
      ['Площадь для оклейки', `${results.netArea.toFixed(2)} м²`],
      ['Тип обоев', getWallpaperTypeName(formData.wallpaperType)],
      ['Ширина рулона', `${formData.rollWidth} см`],
      ['Длина рулона', `${formData.rollLength} м`],
      ['Раппорт', `${formData.patternRepeat} см`],
      ['Запас материала', `${formData.wastagePercent}%`],
      ['Требуется рулонов', results.rollsRequired],
      ['Требуется упаковок клея', results.gluePacksRequired],
      ['Стоимость обоев', `${results.rollsCost.toFixed(2)} руб.`],
      ['Стоимость клея', `${results.glueCost.toFixed(2)} руб.`],
    ];

    // Добавляем грунтовку, если она включена
    if (formData.includePrimeCoat) {
      rows.push(['Стоимость грунтовки', `${results.primeCoatCost.toFixed(2)} руб.`]);
    }

    // Добавляем итоговую стоимость
    rows.push(['ИТОГО', `${results.totalCost.toFixed(2)} руб.`]);

    // Формируем CSV контент
    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + rows.map(row => row.join(';')).join('\n');
    
    // Создаем и скачиваем файл
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_обоев.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Вспомогательная функция для получения названия типа обоев
  const getWallpaperTypeName = (typeId) => {
    const type = wallpaperTypes.find(t => t.id === typeId);
    return type ? type.name : 'Стандартные обои';
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateRollsNeeded();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Wallpaper className="w-6 h-6 text-indigo-500" />
          Параметры расчета обоев
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Размеры комнаты */}
        <RoomParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Параметры обоев */}
        <WallpaperParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          wallpaperTypes={wallpaperTypes}
        />

        {/* Параметры проемов (окна, двери) */}
        <OpeningsParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          glueTypes={glueTypes}
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать количество обоев'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать результаты (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;